/**
 * Optional extension bridge.
 *
 * The extension is pure progressive enhancement: with it installed, inbound
 * text reaches the OS clipboard with no tab focused. The web app detects it and
 * hands over the session so the user only pairs once — and quietly drops the
 * "focus this tab to copy" hint, because it no longer applies.
 */

import type { Session } from './api.js';

/** Published Chrome Web Store id. Overridable for a locally-loaded unpacked build. */
const EXTENSION_ID =
  (import.meta.env.VITE_EXTENSION_ID as string | undefined) ?? 'slipwayextensionidplaceholder';

type ChromeRuntime = {
  runtime?: {
    sendMessage: (
      id: string,
      msg: unknown,
      cb: (res: { ok?: boolean; version?: string } | undefined) => void,
    ) => void;
    lastError?: unknown;
  };
};

function runtime() {
  return (globalThis as unknown as { chrome?: ChromeRuntime }).chrome?.runtime;
}

/** Resolves false quickly when the extension is absent or not reachable. */
export function detectExtension(timeoutMs = 600): Promise<boolean> {
  const rt = runtime();
  if (!rt?.sendMessage) return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;
    const done = (v: boolean) => {
      if (!settled) {
        settled = true;
        resolve(v);
      }
    };
    setTimeout(() => done(false), timeoutMs);
    try {
      rt.sendMessage(EXTENSION_ID, { type: 'ping' }, (res) => {
        // Reading lastError suppresses Chrome's "unchecked runtime.lastError".
        void rt.lastError;
        done(res?.ok === true);
      });
    } catch {
      done(false);
    }
  });
}

export function handOffSession(session: Session, apiBase: string): Promise<boolean> {
  const rt = runtime();
  if (!rt?.sendMessage) return Promise.resolve(false);

  return new Promise((resolve) => {
    try {
      rt.sendMessage(EXTENSION_ID, { type: 'adopt', session, apiBase }, (res) => {
        void rt.lastError;
        resolve(res?.ok === true);
      });
    } catch {
      resolve(false);
    }
  });
}
